import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Package, CheckCircle, Send, Trash2, Tag, Palette, Search, ChevronDown, X, AlertCircle, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import StaffIdInput from '@/components/StaffIdInput';
import { LineProgress, isPcs, overMsg } from '@/components/po/PoLinePicker';
import { SortPoRows, overflowAsk } from '@/components/po/SortPoRows';
import { NextPoDialog } from '@/components/po/NextPoDialog';

const SortAssignment = () => {
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [staffIdLocked, setStaffIdLocked] = useState(false);
  
  // Vendor selection with search
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [vendorPopoverOpen, setVendorPopoverOpen] = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');
  
  // Sort No selection with search
  const [sortNos, setSortNos] = useState([]);
  const [selectedSortNo, setSelectedSortNo] = useState('');
  const [sortNoPopoverOpen, setSortNoPopoverOpen] = useState(false);
  const [sortNoSearch, setSortNoSearch] = useState('');
  
  const [vendorSortLocked, setVendorSortLocked] = useState(false);

  // Purchase Order row the supplier-delivery bundles are counted on (rows of the picked sort no; a bundle can move to the next row)
  const [poSel, setPoSel] = useState({ po: null, line: null, pct: 5, none: false, rows: [] });
  const [poKey, setPoKey] = useState(0);
  const [ask, setAsk] = useState(null);
  
  // Color selection with search
  const [colors, setColors] = useState([]);
  const [selectedColor, setSelectedColor] = useState('');
  const [colorPopoverOpen, setColorPopoverOpen] = useState(false);
  const [colorSearch, setColorSearch] = useState('');
  
  // Bundle scanning - grouped by color
  const [bundleCode, setBundleCode] = useState('');
  const [bundlesByColor, setBundlesByColor] = useState({}); // { colorCode: [bundles] }
  const [isValidatingBundle, setIsValidatingBundle] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Admin override dialog for vendor mismatch
  const [vendorMismatchDialog, setVendorMismatchDialog] = useState({
    open: false,
    bundleBarcode: '',
    bundleVendor: '',
    selectedVendor: ''
  });
  
  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);

  useEffect(() => {
    staffInputRef.current?.focus();
    fetchVendors();
    fetchColors();
  }, []);

  // Focus bundle input when color is selected
  useEffect(() => {
    if (selectedColor && vendorSortLocked) {
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    }
  }, [selectedColor, vendorSortLocked]);

  const fetchVendors = async () => {
    try {
      // Get vendors that have bundles available for Sort No Assignment
      const response = await apiClient.get('/sections/greige/vendor-master');
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchColors = async () => {
    try {
      const response = await apiClient.get('/sections/greige/colors');
      setColors(response.data.colors || []);
    } catch (error) {
      console.error('Error fetching colors:', error);
    }
  };

  const handleVendorSelect = async (vendor) => {
    setSelectedVendor(vendor);
    setVendorPopoverOpen(false);
    setVendorSearch('');
    setSelectedSortNo('');
    setSortNos([]);
    setPoSel({ po: null, line: null, pct: 5, none: false, rows: [] });
    
    if (!vendor) return;
    
    try {
      const response = await apiClient.get('/sections/greige/sort-nos', {
        params: { vendor_name: vendor }
      });
      setSortNos(response.data.sort_nos || []);
    } catch (error) {
      console.error('Error fetching sort nos:', error);
      toast.error('Failed to load sort numbers');
    }
  };

  // Rows come from the picked sort no; when the row in use closes (PO completed) the next pending row is taken
  const handleRowsChange = (sel) => {
    if (sel.switched && vendorSortLocked) {
      if (sel.line) toast.info(`${poSel.po?.po_number || 'The PO'} is no longer open — bundles now count on ${sel.po.po_number}`, { duration: 8000 });
      else {
        setVendorSortLocked(false);
        setSelectedColor('');
        setBundlesByColor({});
        toast.info(`${poSel.po?.po_number || 'The PO'} is fully received or closed — pick the next Purchase Order row and scan the bundles again`, { duration: 8000 });
      }
    }
    setPoSel(sel);
  };
  const poLine = poSel.po && poSel.line ? poSel.line : null;
  const rowOf = (id) => (poSel.rows || []).find((r) => r.line.po_item_id === id) || null;
  const allScanned = () => Object.values(bundlesByColor).flat();
  const pcsOf = (b) => isPcs(rowOf(b.lineId)?.line.unit);
  const poQtyOf = (b) => (pcsOf(b) ? parseFloat(b.qty) : parseFloat(b.poQty)) || 0;
  const qtySum = (list) => Math.round(list.reduce((s, b) => s + (parseFloat(b.qty) || 0), 0) * 1000) / 1000;
  const usedOn = (id, except) => allScanned().filter((b) => b.po && b.lineId === id && b.barcode !== except).reduce((t, b) => t + poQtyOf(b), 0);
  const patchBundle = (barcode, p) => setBundlesByColor((prev) => Object.fromEntries(Object.entries(prev)
    .map(([c, list]) => [c, list.map((b) => (b.barcode === barcode ? { ...b, ...p } : b))])));

  // A bundle's qty past what is pending on its row → ask for the next pending PO (the whole bundle moves)
  const checkBundle = (barcode, lineId) => {
    const b = allScanned().find((x) => x.barcode === barcode);
    const row = b && b.po ? rowOf(lineId || b.lineId) : null;
    const q = row ? (isPcs(row.line.unit) ? parseFloat(b.qty) : parseFloat(b.poQty)) || 0 : 0;
    if (!row || !(q > 0)) return false;
    const a = overflowAsk({ row, rows: poSel.rows, qty: q, usedOn: (id) => usedOn(id, barcode), what: `Bundle ${barcode}` });
    setAsk(a ? { ...a, barcode } : null);
    return !!a;
  };
  const onNextPo = (r) => {
    const { barcode } = ask;
    patchBundle(barcode, { lineId: r.line.po_item_id });
    setPoSel((s) => ({ ...s, po: r.po, line: r.line, none: false }));
    if (!checkBundle(barcode, r.line.po_item_id)) toast.success(`${barcode} is counted on ${r.po.po_number} — the next bundles go there too`);
  };
  const onRemovePo = () => {
    const color = Object.keys(bundlesByColor).find((c) => bundlesByColor[c].some((b) => b.barcode === ask.barcode));
    if (color) handleRemoveBundle(color, ask.barcode);
    setAsk(null);
  };
  const onCancelPo = () => { patchBundle(ask.barcode, isPcs(ask.row.line.unit) ? { qty: '' } : { poQty: '' }); setAsk(null); };

  // Filter vendors based on search
  const filteredVendors = useMemo(() => {
    if (!vendorSearch) return vendors;
    const search = vendorSearch.toLowerCase();
    return vendors.filter(v => v.toLowerCase().includes(search));
  }, [vendors, vendorSearch]);

  // Filter sort nos based on search
  const filteredSortNos = useMemo(() => {
    if (!sortNoSearch) return sortNos;
    const search = sortNoSearch.toLowerCase();
    return sortNos.filter(s => 
      s.sort_no.toLowerCase().includes(search) ||
      (s.design_name && s.design_name.toLowerCase().includes(search)) ||
      (s.description && s.description.toLowerCase().includes(search))
    );
  }, [sortNos, sortNoSearch]);

  // Filter colors based on search
  const filteredColors = useMemo(() => {
    if (!colorSearch) return colors;
    const search = colorSearch.toLowerCase();
    return colors.filter(color => 
      color.color_name.toLowerCase().includes(search) ||
      `${color.red_code}-${color.green_code}-${color.blue_code}`.includes(search)
    );
  }, [colors, colorSearch]);

  const handleStaffValidated = (result) => {
    if (result.valid) {
      setStaffId(result.staffId);
      setStaffName(result.staffName || '');
      setStaffIdLocked(true);
    }
  };

  const handleSortNoSelect = (sortNo) => {
    setSelectedSortNo(sortNo);
    setSortNoPopoverOpen(false);
    setSortNoSearch('');
    setPoSel((s) => ({ ...s, po: null, line: null, none: false }));
  };

  const handleVendorSortLock = () => {
    if (!selectedVendor || !selectedSortNo) {
      toast.error('Please select vendor and sort no');
      return;
    }
    if ((poSel.rows || []).length && !poSel.line && !poSel.none) {
      toast.error(`Pick the Purchase Order row of sort no ${selectedSortNo} these bundles are counted on`);
      return;
    }
    setVendorSortLocked(true);
  };

  // Format color display with RGB
  const formatColorDisplay = (color) => {
    return `${color.color_name} (${color.red_code}-${color.green_code}-${color.blue_code})`;
  };

  const handleColorSelect = (colorCode) => {
    setSelectedColor(colorCode);
    setColorPopoverOpen(false);
    setColorSearch('');
  };

  const getSelectedColorData = () => {
    return colors.find(c => c.color_code === selectedColor);
  };

  const getSelectedSortNoData = () => {
    return sortNos.find(s => s.sort_no === selectedSortNo);
  };

  const handleBundleScan = async (e) => {
    e.preventDefault();
    
    if (!selectedColor) {
      toast.error('Please select a color first');
      setBundleCode('');
      return;
    }
    
    const code = bundleCode.trim();
    if (!code) {
      toast.error('Please scan bundle barcode');
      setBundleCode('');
      bundleInputRef.current?.focus();
      return;
    }

    // Check if bundle already scanned in any color in current session
    const allBundles = Object.values(bundlesByColor).flat().map(b => b.barcode);
    if (allBundles.includes(code)) {
      toast.error('Bundle already scanned in this session');
      setBundleCode('');
      bundleInputRef.current?.focus();
      return;
    }

    await validateAndAddBundle(code, false);
  };

  const validateAndAddBundle = async (code, adminOverride = false) => {
    setIsValidatingBundle(true);
    try {
      // Validate bundle - must be valid system code, not already processed, and match vendor
      const response = await apiClient.post('/sections/greige/sort-assignment/validate-bundle', null, {
        params: { 
          bundle_barcode: code,
          vendor_name: selectedVendor,
          admin_override: adminOverride,
          ...(poLine ? { po_item_id: poLine.po_item_id } : {}),
        }
      });
      
      // Add bundle to the current color's list (with empty qty to be entered)
      setBundlesByColor(prev => ({
        ...prev,
        [selectedColor]: [{ barcode: code, qty: '', poQty: '', po: !!response.data.po_counted, lineId: poLine?.po_item_id || null }, ...(prev[selectedColor] || [])]
      }));
      
      const colorData = getSelectedColorData();
      const totalBundles = Object.values(bundlesByColor).flat().length + 1;
      
      if (response.data.admin_override_used) {
        toast.warning(`Bundle added with admin override! Originally from: ${response.data.bundle_vendor}`);
      } else {
        toast.success(`Bundle added to ${colorData?.color_name}! Total: ${totalBundles}`);
      }
      setBundleCode('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to validate bundle';
      
      // Check if it's a vendor mismatch error
      if (errorMsg.includes('does not belong to this vendor')) {
        // Extract the actual vendor name from the error message
        const match = errorMsg.match(/Bundle belongs to: (.+)$/);
        const bundleVendor = match ? match[1] : 'Unknown Vendor';
        
        // Show admin override dialog
        setVendorMismatchDialog({
          open: true,
          bundleBarcode: code,
          bundleVendor: bundleVendor,
          selectedVendor: selectedVendor
        });
      } else {
        toast.error(errorMsg);
        if (poLine && /short-closed|pick an open PO/.test(errorMsg)) setPoKey((k) => k + 1);
      }
      setBundleCode('');
    } finally {
      setIsValidatingBundle(false);
      setTimeout(() => bundleInputRef.current?.focus(), 50);
    }
  };

  const handleAdminOverride = async () => {
    const { bundleBarcode } = vendorMismatchDialog;
    setVendorMismatchDialog({ open: false, bundleBarcode: '', bundleVendor: '', selectedVendor: '' });
    
    // Re-validate with admin override
    await validateAndAddBundle(bundleBarcode, true);
  };

  const handleCancelOverride = () => {
    setVendorMismatchDialog({ open: false, bundleBarcode: '', bundleVendor: '', selectedVendor: '' });
    bundleInputRef.current?.focus();
  };

  const handleRemoveBundle = (colorCode, bundle) => {
    setBundlesByColor(prev => {
      const updated = { ...prev };
      updated[colorCode] = updated[colorCode].filter(b => b.barcode !== bundle);
      // Remove color key if no bundles left
      if (updated[colorCode].length === 0) {
        delete updated[colorCode];
      }
      return updated;
    });
    toast.success('Bundle removed');
  };

  const setBundleQty = (colorCode, barcode, value, field = 'qty') => {
    const v = value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    setBundlesByColor(prev => ({
      ...prev,
      [colorCode]: (prev[colorCode] || []).map(b => b.barcode === barcode ? { ...b, [field]: v } : b),
    }));
  };

  const handleRemoveColorGroup = (colorCode) => {
    setBundlesByColor(prev => {
      const updated = { ...prev };
      delete updated[colorCode];
      return updated;
    });
    toast.success('Color group removed');
  };

  const getTotalBundles = () => {
    return Object.values(bundlesByColor).flat().length;
  };

  const getColorGroups = () => {
    return Object.entries(bundlesByColor).map(([colorCode, bundles]) => {
      const colorData = colors.find(c => c.color_code === colorCode);
      return {
        colorCode,
        colorData,
        bundles
      };
    });
  };

  const handleSubmit = async () => {
    const totalBundles = getTotalBundles();
    if (totalBundles === 0) {
      toast.error('No bundles scanned');
      return;
    }

    // Every bundle must have a piece qty > 0
    const colorGroups = getColorGroups();
    const invalidQty = [];
    for (const group of colorGroups) {
      for (const b of group.bundles) {
        if (!(parseFloat(b.qty) > 0)) invalidQty.push(b.barcode);
      }
    }
    if (invalidQty.length > 0) {
      toast.error(`Enter a valid Qty (> 0) for: ${invalidQty.slice(0, 3).join(', ')}${invalidQty.length > 3 ? '…' : ''}`);
      return;
    }
    const onPo = colorGroups.flatMap(g => g.bundles).filter(b => b.po && b.lineId);
    const noMtr = onPo.filter(b => !pcsOf(b) && !(parseFloat(b.poQty) > 0)).map(b => b.barcode);
    if (noMtr.length) {
      toast.error(`Enter the ${rowOf(onPo[0].lineId)?.line.unit || 'PO qty'} for: ${noMtr.slice(0, 3).join(', ')}${noMtr.length > 3 ? '…' : ''}`);
      return;
    }
    // Bundles in scan order: the first one past its row's pending qty asks for the next PO (or is blocked)
    const used = {};
    for (const b of colorGroups.flatMap(g => [...g.bundles].reverse()).filter(x => x.po && x.lineId)) {
      const row = rowOf(b.lineId);
      if (!row) continue;
      const a = overflowAsk({ row, rows: poSel.rows, qty: poQtyOf(b), usedOn: (id) => used[id] || 0, what: `Bundle ${b.barcode}` });
      if (a) { setAsk({ ...a, barcode: b.barcode }); return; }
      used[b.lineId] = (used[b.lineId] || 0) + poQtyOf(b);
    }
    for (const id of [...new Set(onPo.map(b => b.lineId))]) {
      const row = rowOf(id);
      const n = onPo.filter(b => b.lineId === id).length;
      const over = row && overMsg(row.po, row.line, poSel.pct, usedOn(id), n > 1 ? `these ${n} bundles` : 'this bundle');
      if (!row) { toast.error('A Purchase Order row of these bundles is no longer open — pick the row again'); return; }
      if (over) { toast.error(over, { duration: 9000 }); return; }
    }

    setIsSubmitting(true);
    const done = new Set();
    const usedPos = new Set();
    try {
      // One submit per colour and PO row (a bundle that moved to the next PO is counted there)
      for (const group of colorGroups) {
        const byLine = {};
        group.bundles.forEach((b) => { (byLine[b.lineId || ''] = byLine[b.lineId || ''] || []).push(b); });
        for (const [lid, list] of Object.entries(byLine)) {
          const pcs = isPcs(rowOf(lid)?.line.unit);
          await apiClient.post('/sections/greige/sort-assignment/submit', {
            staff_id: staffId,
            vendor_name: selectedVendor,
            sort_no: selectedSortNo,
            color_code: group.colorCode,
            bundle_barcodes: list.map(b => b.barcode),
            bundle_quantities: list.map(b => ({
              bundle_barcode: b.barcode, qty: parseFloat(b.qty),
              ...(b.po && lid && !pcs ? { po_qty: parseFloat(b.poQty) } : {}),
            })),
            ...(lid ? { po_item_id: lid } : {}),
          });
          list.forEach(b => done.add(b.barcode));
          if (lid && list.some(b => b.po)) usedPos.add(rowOf(lid)?.po.po_number);
        }
      }

      toast.success(`Sort assignment recorded! ${done.size} bundles assigned across ${colorGroups.length} color(s)${usedPos.size ? ` · counted on ${[...usedPos].join(', ')}` : ''}`);
      setPoKey((k) => k + 1);
      setBundlesByColor({});
      setSelectedColor('');
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit', { duration: 9000 });
      if (done.size) {
        setBundlesByColor((prev) => Object.fromEntries(Object.entries(prev).map(([c, l]) => [c, l.filter(b => !done.has(b.barcode))]).filter(([, l]) => l.length)));
        toast.info(`${done.size} bundle(s) were saved before the error — only the rest are left to submit`);
      }
      setPoKey((k) => k + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setStaffIdLocked(false);
    setStaffId('');
    setSelectedVendor('');
    setSelectedSortNo('');
    setVendorSortLocked(false);
    setSelectedColor('');
    setBundlesByColor({});
    setPoSel({ po: null, line: null, pct: 5, none: false, rows: [] });
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/sections/greige')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Operation 2: Sort No Assignment</h1>
              <p className="text-sm text-[#E8DCC8]">Greige Section - Assign Vendor, Sort & Color</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Step 1: Staff ID */}
        <Card className="border-2 border-[#E5DFD0]">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold bg-blue-600">1</div>
              <User className="w-5 h-5 text-blue-600" />
              Staff Identification
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!staffIdLocked ? (
              <StaffIdInput
                inputRef={staffInputRef}
                onValidated={handleStaffValidated}
                autoFocus={true}
                label=""
              />
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-600 text-white px-4 py-2 rounded-lg font-mono text-lg">{staffId}</div>
                  {staffName && <span className="text-[#5A5F6E]">{staffName}</span>}
                </div>
                <Button variant="outline" onClick={handleReset}>Change</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Vendor & Sort No Selection with Search */}
        {staffIdLocked && (
          <Card className={`border-2 ${vendorSortLocked ? 'border-green-500 bg-green-50/30' : 'border-blue-600'}`}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold bg-blue-600">2</div>
                <Tag className="w-5 h-5 text-blue-600" />
                Vendor & Sort No
              </CardTitle>
              <CardDescription>Search and select from master data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!vendorSortLocked ? (
                <>
                  <div className="grid grid-cols-1 gap-4">
                    {/* Vendor Selection with Search */}
                    <div>
                      <Label>Vendor Name * (Searchable)</Label>
                      <Popover open={vendorPopoverOpen} onOpenChange={setVendorPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            className="w-full h-12 mt-1 justify-between text-left font-normal"
                            data-testid="vendor-select"
                          >
                            <span className={selectedVendor ? "text-gray-900" : "text-gray-500"}>
                              {selectedVendor || "Search vendor..."}
                            </span>
                            <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[350px] p-0" align="start">
                          <Command>
                            <CommandInput 
                              placeholder="Search vendor name..." 
                              value={vendorSearch}
                              onValueChange={setVendorSearch}
                            />
                            <CommandList className="max-h-[300px]">
                              <CommandEmpty>No vendor found.</CommandEmpty>
                              <CommandGroup>
                                {filteredVendors.map((vendor) => (
                                  <CommandItem
                                    key={vendor}
                                    value={vendor}
                                    onSelect={() => handleVendorSelect(vendor)}
                                    className="cursor-pointer"
                                  >
                                    {vendor}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    {/* Sort No Selection with Search */}
                    <div>
                      <Label>Sort No * (Searchable)</Label>
                      <Popover open={sortNoPopoverOpen} onOpenChange={setSortNoPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            className="w-full h-12 mt-1 justify-between text-left font-normal"
                            disabled={!selectedVendor}
                            data-testid="sortno-select"
                          >
                            <span className={selectedSortNo ? "text-gray-900" : "text-gray-500"}>
                              {selectedSortNo ? (
                                <>
                                  {selectedSortNo}
                                  {getSelectedSortNoData()?.design_name && (
                                    <span className="text-purple-600 ml-2">- {getSelectedSortNoData().design_name}</span>
                                  )}
                                </>
                              ) : (
                                selectedVendor ? "Search sort no..." : "Select vendor first"
                              )}
                            </span>
                            <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[350px] p-0" align="start">
                          <Command>
                            <CommandInput 
                              placeholder="Search sort no or design name..." 
                              value={sortNoSearch}
                              onValueChange={setSortNoSearch}
                            />
                            <CommandList className="max-h-[300px]">
                              <CommandEmpty>No sort no found for this vendor.</CommandEmpty>
                              <CommandGroup>
                                {filteredSortNos.map((sort) => (
                                  <CommandItem
                                    key={sort.sort_no}
                                    value={`${sort.sort_no} ${sort.design_name || ''} ${sort.description || ''}`}
                                    onSelect={() => handleSortNoSelect(sort.sort_no)}
                                    className="cursor-pointer"
                                  >
                                    <div>
                                      <p className="font-medium">{sort.sort_no}</p>
                                      {sort.design_name ? (
                                        <p className="text-xs text-purple-600 font-medium">{sort.design_name}</p>
                                      ) : sort.description ? (
                                        <p className="text-xs text-gray-500">{sort.description}</p>
                                      ) : null}
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Pending Purchase Order rows of the sort no (supplier deliveries are counted on the row picked) */}
                    {selectedVendor && (
                      <div data-testid="greige-po-block">
                        <Label>Pending Purchase Orders{selectedSortNo ? ` — sort no ${selectedSortNo}` : ''}</Label>
                        <p className="text-xs text-gray-500 mb-1">Pending qty and rate per PO row. When a bundle's qty goes past the pending qty you're asked for the next PO.</p>
                        <SortPoRows section="Greige" vendorName={selectedVendor} sortNo={selectedSortNo} lineId={poSel.line?.po_item_id} none={poSel.none}
                          onChange={handleRowsChange} refreshKey={poKey} pendingOf={(id) => usedOn(id)} testPrefix="greige-po" />
                      </div>
                    )}
                  </div>

                  {/* Category 2 (item_master) + Size (greige_sort_nos) for the selected sort no */}
                  {selectedSortNo && (
                    <div className="grid grid-cols-2 gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100" data-testid="sortno-details">
                      <div>
                        <span className="text-xs text-purple-500 font-medium block">Category 2</span>
                        <span className="text-sm font-semibold text-gray-900" data-testid="sortno-category2">
                          {getSelectedSortNoData()?.category2 || '—'}
                        </span>
                      </div>
                      <div>
                        <span className="text-xs text-purple-500 font-medium block">Size</span>
                        <span className="text-sm font-semibold text-gray-900" data-testid="sortno-size">
                          {getSelectedSortNoData()?.size || '—'}
                        </span>
                      </div>
                    </div>
                  )}
                  <Button 
                    onClick={handleVendorSortLock} 
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700"
                    disabled={!selectedVendor || !selectedSortNo}
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Lock Vendor & Sort No
                  </Button>
                </>
              ) : (
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div>
                      <span className="text-sm text-blue-600 font-medium">Vendor:</span>
                      <span className="font-semibold text-blue-900 ml-2">{selectedVendor}</span>
                    </div>
                    <div className="border-l border-blue-200 pl-4">
                      <span className="text-sm text-blue-600 font-medium">Sort No:</span>
                      <span className="font-mono font-semibold text-blue-900 ml-2">{selectedSortNo}</span>
                    </div>
                    <div className="border-l border-blue-200 pl-4">
                      <span className="text-sm text-blue-600 font-medium">Category 2:</span>
                      <span className="font-semibold text-blue-900 ml-2" data-testid="locked-category2">{getSelectedSortNoData()?.category2 || '—'}</span>
                    </div>
                    <div className="border-l border-blue-200 pl-4">
                      <span className="text-sm text-blue-600 font-medium">Size:</span>
                      <span className="font-semibold text-blue-900 ml-2" data-testid="locked-size">{getSelectedSortNoData()?.size || '—'}</span>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => { setVendorSortLocked(false); setSelectedColor(''); setBundlesByColor({}); }} data-testid="vendor-sort-change-btn">
                    Change
                  </Button>
                </div>
              )}
              {vendorSortLocked && (
                <div className="mt-3 p-3 rounded-lg border bg-white space-y-2" data-testid="locked-po-line">
                  {poLine ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                      <div className="text-sm shrink-0">
                        <span className="text-blue-600 font-medium">Counting on PO:</span> <span className="font-mono font-semibold" data-testid="locked-po-number">{poSel.po.po_number}</span>
                        <span className="text-gray-400"> · </span><span className="font-medium">{poLine.supplier_design_name}</span>
                        <span className="text-gray-400"> · </span><span className="text-xs text-gray-600">₹{poLine.rate ?? 0}/{poLine.unit}</span>
                      </div>
                      <LineProgress line={poLine} pending={usedOn(poLine.po_item_id)} testId="locked-po-progress" />
                    </div>
                  ) : (
                    <p className="text-xs text-gray-600">No PO row picked — only bundles that didn't come from a supplier delivery (returns, transfers) can be assigned.</p>
                  )}
                  {(poSel.rows || []).length > 1 && (
                    <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="locked-po-rows">
                      {poSel.rows.map((r) => (
                        <span key={r.line.po_item_id} className={`rounded border px-1.5 py-0.5 ${r.line.po_item_id === poLine?.po_item_id ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-200 text-gray-600'}`}>
                          {r.po.po_number}: {Math.max(0, Math.round(((Number(r.line.balance_qty) || 0) - usedOn(r.line.po_item_id)) * 1000) / 1000)} {r.line.unit} pending
                        </span>))}
                    </div>)}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Color Selection & Bundle Scanning */}
        {vendorSortLocked && (
          <Card className="border-2 border-blue-600">
            <CardHeader className="pb-3 bg-blue-50">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold bg-blue-600">3</div>
                <Palette className="w-5 h-5 text-blue-600" />
                Select Color & Scan Bundles
              </CardTitle>
              <CardDescription>Select a color, scan bundles, then change color to continue. Bundles already processed cannot be rescanned.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              {/* Color Selection with Search */}
              <div>
                <Label>Color * (Search by name or RGB)</Label>
                <Popover open={colorPopoverOpen} onOpenChange={setColorPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full h-12 mt-1 justify-between text-left font-normal"
                      data-testid="color-select"
                    >
                      {selectedColor ? (
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-6 h-6 rounded border border-gray-300 flex-shrink-0" 
                            style={{backgroundColor: `rgb(${getSelectedColorData()?.red_code}, ${getSelectedColorData()?.green_code}, ${getSelectedColorData()?.blue_code})`}}
                          />
                          <span>{formatColorDisplay(getSelectedColorData())}</span>
                        </div>
                      ) : (
                        <span className="text-gray-500">Search and select color...</span>
                      )}
                      <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command>
                      <CommandInput 
                        placeholder="Search color by name or RGB..." 
                        value={colorSearch}
                        onValueChange={setColorSearch}
                      />
                      <CommandList className="max-h-[300px]">
                        <CommandEmpty>No color found.</CommandEmpty>
                        <CommandGroup>
                          {filteredColors.map((color) => (
                            <CommandItem
                              key={color.color_code}
                              value={`${color.color_name} ${color.red_code}-${color.green_code}-${color.blue_code}`}
                              onSelect={() => handleColorSelect(color.color_code)}
                              className="cursor-pointer"
                            >
                              <div className="flex items-center gap-3 w-full">
                                <div 
                                  className="w-8 h-8 rounded border border-gray-300 flex-shrink-0" 
                                  style={{backgroundColor: `rgb(${color.red_code}, ${color.green_code}, ${color.blue_code})`}}
                                />
                                <div className="flex-1">
                                  <p className="font-medium">{color.color_name}</p>
                                  <p className="text-xs text-gray-500">({color.red_code}-{color.green_code}-{color.blue_code})</p>
                                </div>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Bundle Scanning Input */}
              {selectedColor && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div 
                      className="w-10 h-10 rounded border-2 border-blue-300" 
                      style={{backgroundColor: `rgb(${getSelectedColorData()?.red_code}, ${getSelectedColorData()?.green_code}, ${getSelectedColorData()?.blue_code})`}}
                    />
                    <div>
                      <p className="font-semibold text-blue-900">{getSelectedColorData()?.color_name}</p>
                      <p className="text-xs text-blue-600">Scanning bundles for this color</p>
                    </div>
                  </div>
                  <form onSubmit={handleBundleScan} className="flex gap-3">
                    <Input 
                      ref={bundleInputRef} 
                      value={bundleCode} 
                      onChange={(e) => setBundleCode(e.target.value)} 
                      placeholder="Scan Bundle QR code..." 
                      className="text-lg h-14 font-mono bg-white" 
                      disabled={isValidatingBundle} 
                      autoComplete="off"
                      data-testid="bundle-input"
                    />
                    <Button 
                      type="submit" 
                      className="h-14 px-8 bg-blue-600 hover:bg-blue-700" 
                      disabled={isValidatingBundle}
                    >
                      {isValidatingBundle ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Package className="w-5 h-5 mr-2" />
                          Add
                        </>
                      )}
                    </Button>
                  </form>
                  <p className="text-xs text-blue-600 mt-2">
                    Bundles for {getSelectedColorData()?.color_name}: {bundlesByColor[selectedColor]?.length || 0}
                  </p>
                </div>
              )}
              
              {/* Warning */}
              <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-700">
                  <p className="font-medium">Important:</p>
                  <p>Only bundles from Bundle Receipt (Operation 1) can be scanned. Once a bundle is processed here, it <strong>cannot be rescanned</strong>.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Scanned Bundles Grouped by Color */}
        {getTotalBundles() > 0 && (
          <Card className="border-2 border-[#E5DFD0]">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Scanned Bundles ({getTotalBundles()})</CardTitle>
                  <CardDescription>
                    {getColorGroups().length} color(s) · {qtySum(Object.values(bundlesByColor).flat())} qty total · Latest scans on top
                  </CardDescription>
                </div>
                <Button onClick={handleSubmit} disabled={isSubmitting} className="bg-green-600 hover:bg-green-700">
                  {isSubmitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Submit All
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {getColorGroups().map(({ colorCode, colorData, bundles }) => (
                <div key={colorCode} className="border-2 border-gray-200 rounded-lg overflow-hidden">
                  {/* Color Header */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 border-b">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-8 h-8 rounded border border-gray-300" 
                        style={{backgroundColor: colorData ? `rgb(${colorData.red_code}, ${colorData.green_code}, ${colorData.blue_code})` : '#ccc'}}
                      />
                      <div>
                        <p className="font-semibold text-gray-900">
                          {colorData ? formatColorDisplay(colorData) : colorCode}
                        </p>
                        <p className="text-xs text-gray-500">
                          {bundles.length} bundle(s) · {qtySum(bundles)} qty
                        </p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleRemoveColorGroup(colorCode)}
                      className="text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Remove All
                    </Button>
                  </div>
                  
                  {/* Bundles List */}
                  <div className="divide-y divide-gray-100 max-h-56 overflow-y-auto">
                    {bundles.map((bundle, index) => (
                      <div key={bundle.barcode} className="flex items-center justify-between gap-2 px-4 py-2 hover:bg-gray-50">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xs text-gray-400 w-6">#{bundles.length - index}</span>
                          <p className="font-mono text-sm font-medium text-gray-900 truncate">{bundle.barcode}</p>
                          {(poSel.rows || []).length > 0 && (bundle.po && bundle.lineId
                            ? <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1" data-testid={`sort-po-tag-${bundle.barcode}`}>{rowOf(bundle.lineId)?.po.po_number || 'PO'}</span>
                            : <span className="text-[10px] text-gray-500 bg-gray-50 border rounded px-1" data-testid={`sort-nopo-tag-${bundle.barcode}`}>not on PO</span>)}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={bundle.qty}
                            onChange={(e) => setBundleQty(colorCode, bundle.barcode, e.target.value)}
                            onBlur={() => pcsOf(bundle) && checkBundle(bundle.barcode)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                            placeholder="Qty"
                            className="w-20 h-9 text-center font-semibold"
                            data-testid={`sort-qty-${bundle.barcode}`}
                          />
                          <span className="text-xs text-gray-400">qty</span>
                          {bundle.po && bundle.lineId && !pcsOf(bundle) && (
                            <>
                              <Input type="text" inputMode="decimal" value={bundle.poQty}
                                onChange={(e) => setBundleQty(colorCode, bundle.barcode, e.target.value, 'poQty')}
                                onBlur={() => checkBundle(bundle.barcode)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                                placeholder={rowOf(bundle.lineId)?.line.unit} className="w-20 h-9 text-center font-semibold" data-testid={`sort-poqty-${bundle.barcode}`} />
                              <span className="text-xs text-gray-400">{rowOf(bundle.lineId)?.line.unit}</span>
                            </>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleRemoveBundle(colorCode, bundle.barcode)} 
                            className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Admin Override Dialog for Vendor Mismatch */}
        <AlertDialog open={vendorMismatchDialog.open} onOpenChange={(open) => !open && handleCancelOverride()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
                <ShieldAlert className="h-5 w-5" />
                Vendor Mismatch Detected
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p className="text-red-600 font-medium">
                    This bundle does not belong to the selected vendor.
                  </p>
                  <div className="bg-slate-50 p-3 rounded-lg space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Bundle:</span>
                      <span className="font-mono font-medium">{vendorMismatchDialog.bundleBarcode}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Bundle's Vendor:</span>
                      <span className="font-medium text-blue-600">{vendorMismatchDialog.bundleVendor}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Selected Vendor:</span>
                      <span className="font-medium text-amber-600">{vendorMismatchDialog.selectedVendor}</span>
                    </div>
                  </div>
                  <p className="text-slate-600">
                    Only <strong>Admins</strong> can override this validation. Do you want to proceed?
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleCancelOverride}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleAdminOverride}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Admin Override
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <NextPoDialog ask={ask} onPick={onNextPo} onCancel={onCancelPo} onRemove={onRemovePo} />
      </main>
    </div>
  );
};

export default SortAssignment;
